import { useState, useEffect } from 'react';
import { Auth, API, graphqlOperation } from 'aws-amplify';
import dayjs from 'dayjs';

export const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

export const checkRole = async () => {
    try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken();
        const groups = idToken.payload['cognito:groups'] || [];
        return groups;
    } catch (error) {
        console.error('Error checking user group:', error);
    }
};

//a hook to get the id token from the current session
export const useIdToken = () => {
    const [idToken, setIdToken] = useState(null);

    useEffect(() => {
        const getIdToken = async () => {
            try {
                const session = await Auth.currentSession();
                const token = session.getIdToken();
                setIdToken(token);
            } catch (error) {
                console.error('Error getting id token:', error);
            }
        };
        getIdToken();
    }, []);

    return idToken;
};

export const useVerifyCustomer = () => {
    const [isCustomer, setIsCustomer] = useState(false);

    useEffect(() => {
        const checkCustomer = async () => {
            const role = await checkRole();
            if (role?.includes('Customer')) {
                setIsCustomer(true);
            }
        };
        checkCustomer();
    }, []);

    return isCustomer;
};
export const useVerifyAdmin = () => {
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const checkAdmin = async () => {
            const role = await checkRole();
            if (role?.includes('Admin')) {
                setIsAdmin(true);
            }
        };
        checkAdmin();
    }, []);

    return isAdmin;
};

//check if CompanyAdmin
export const useVerifyCompanyAdmin = () => {
    const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);

    useEffect(() => {
        const checkCompanyAdmin = async () => {
            const role = await checkRole();
            if (role?.includes('CompanyAdmin')) {
                setIsCompanyAdmin(true);
            }
        };
        checkCompanyAdmin();
    }, []);

    return isCompanyAdmin;
};

export const useVerifyInstaller = () => {
    const [isInstaller, setIsInstaller] = useState(false);

    useEffect(() => {
        const checkInstaller = async () => {
            const role = await checkRole();
            if (role?.includes('Installer')) {
                setIsInstaller(true);
            }
        };
        checkInstaller();
    }, []);

    return isInstaller;
};

export const useSitesPermission = async () => {
    const [canManageSites, setCanManageSites] = useState(false);

    useEffect(() => {
        const checkSitesPermission = async () => {
            const role = await checkRole();
            if (
                role?.includes('Admin') ||
                role?.includes('Installer') ||
                role?.includes('CompanyAdmin')
            ) {
                setCanManageSites(true);
            }
        };
        checkSitesPermission();
    }, []);

    return canManageSites;
};

//allows for caching the same queries in session storage
//@todo remove duplicate code of queryAdapter
export const queryAdapter = async (
    graphqlQuery,
    queryParams,
    cache = false,
    authToken = null
) => {
    const queryString = JSON.stringify(queryParams) + graphqlQuery;
    let response = null;
    cache = false; // we have several bugs where if cache is enabled it causes problems
    if (cache && sessionStorage.getItem(queryString)) {
        response = sessionStorage.getItem(queryString);
        response = JSON.parse(response);
    } else {
        response = await API.graphql(
            graphqlOperation(graphqlQuery, queryParams, authToken)
        );
        sessionStorage.setItem(queryString, JSON.stringify(response));
    }
    return response;
};

export const useJwtToken = () => {
    const [jwtToken, setJwtToken] = useState(null);

    useEffect(() => {
        const getJwtToken = async () => {
            try {
                const session = await Auth.currentSession();
                const token = session.getAccessToken().getJwtToken();
                setJwtToken(token);
            } catch (error) {
                console.error('Error getting JWT token:', error);
            }
        };
        getJwtToken();
    }, []);

    return jwtToken;
};

export const logActivity = async (eventType, level, action, message, data) => {
    const userAccessToken = (await Auth.currentSession())
        .getAccessToken()
        .getJwtToken();

    const body = JSON.stringify({
        timestamp: dayjs().valueOf(),
        eventType,
        level,
        action,
        message,
        metadata: data,
    });

    try {
        const newUserEvent = await fetch(`${API_ENDPOINT}/log`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: userAccessToken,
            },
            body,
        });

        if (!newUserEvent.ok) {
            const error = await newUserEvent.json();
            throw new Error(error.message);
        }
        return newUserEvent;
    } catch (error) {
        console.error('Error logging user activity:', error);
    }
};
